/**
 * useDeckGrant — mint the deck-access grant cookie for inline slides BEFORE
 * their <img> requests fire.
 *
 * Background: gated (non-free) modules require a `deck_access` cookie, minted
 * by POST /api/v1/decks/{slug}/grant (`grantDeckAccess`), before the tier-gated
 * `/decks/{slug}/slides/...` route will serve images or downloads. That grant
 * used to be minted by <EmbeddedSlideDeck> when the learner opened the deck
 * card — but inline-slides modules suppress that component entirely (slides
 * render inline in the lesson body instead), so nothing ever minted the grant
 * and every inline slide image / download 403'd.
 *
 * This hook centralizes the "is a grant needed, and has it landed yet" state
 * so the page can gate rendering of <SectionSlides> (and the download row)
 * until the cookie is actually set — otherwise above-the-fold slides can fire
 * their image requests in the window before the grant resolves.
 */

import { useEffect, useState } from "react";
import { grantDeckAccess } from "../api/client";

export interface UseDeckGrantOptions {
  /** Whether this module actually needs a minted grant before slides can load. */
  needsGrant: boolean;
}

export interface UseDeckGrantResult {
  /** True once slides are safe to render: either no grant was needed, or the
   * grant call resolved successfully. Stays false forever if the grant fails. */
  deckReady: boolean;
}

export function useDeckGrant(
  slug: string | undefined,
  { needsGrant }: UseDeckGrantOptions
): UseDeckGrantResult {
  const [deckReady, setDeckReady] = useState(!needsGrant);

  useEffect(() => {
    if (!needsGrant) {
      setDeckReady(true);
      return;
    }
    if (!slug) return;

    let alive = true;
    setDeckReady(false);
    grantDeckAccess(slug)
      .then(() => {
        if (alive) setDeckReady(true);
      })
      .catch(() => {
        // Grant failed — leave deckReady false. Inline slides simply don't
        // render; the lesson text itself still renders fine.
      });
    return () => {
      alive = false;
    };
  }, [slug, needsGrant]);

  return { deckReady };
}
